import React, { useEffect, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  Stack,
  Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import DownloadIcon from '@mui/icons-material/Download';
import { renderAsync } from 'docx-preview';

import { getAnonymizationRunDocument } from '../../services/anonymizationRunsService';

const isDocx = (name = '', type = '') =>
  name.toLowerCase().endsWith('.docx') ||
  type.includes('officedocument.wordprocessingml');

const isPdf = (name = '', type = '') =>
  name.toLowerCase().endsWith('.pdf') || type.includes('pdf');

const isImage = (name = '', type = '') =>
  type.startsWith('image/') || /\.(png|jpe?g|gif|bmp|tiff?|webp)$/i.test(name);

/**
 * Náhľad originálu kontrolovaného dokumentu.
 *
 * Dokument sa sťahuje až na kliknutie (nikdy nie hromadne v listingu) a prístup
 * je na backende auditovaný. Obsahuje NEANONYMIZOVANÉ údaje — preto výstraha.
 */
const ValidationDocumentPreviewDialog = ({ open, onClose, run }) => {
  const docxRef = useRef(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [doc, setDoc] = useState(null); // { blob, filename, contentType, url }

  const runId = run?.id;
  const fallbackName = run?.input_filename || '';

  useEffect(() => {
    if (!open || !runId) return undefined;

    let alive = true;
    let objectUrl = null;

    setLoading(true);
    setError(null);
    setDoc(null);

    (async () => {
      try {
        const result = await getAnonymizationRunDocument(runId);
        if (!alive) return;

        objectUrl = URL.createObjectURL(result.blob);
        setDoc({ ...result, url: objectUrl });
      } catch (e) {
        if (!alive) return;
        setError(e.message || 'Dokument sa nepodarilo načítať.');
      } finally {
        if (alive) setLoading(false);
      }
    })();

    return () => {
      alive = false;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [open, runId]);

  const name = doc?.filename || fallbackName;
  const type = doc?.contentType || '';

  // DOCX sa vykresľuje cez docx-preview do vlastného kontajnera.
  useEffect(() => {
    if (!doc || !isDocx(name, type) || !docxRef.current) return;

    let alive = true;
    (async () => {
      try {
        const buffer = await doc.blob.arrayBuffer();
        if (!alive || !docxRef.current) return;
        docxRef.current.innerHTML = '';
        await renderAsync(buffer, docxRef.current, null, {
          className: 'docx-viewer',
          inWrapper: true,
        });
      } catch (e) {
        if (alive) setError(e.message || 'Dokument sa nepodarilo vykresliť.');
      }
    })();

    return () => {
      alive = false;
    };
  }, [doc, name, type]);

  const renderBody = () => {
    if (loading) {
      return (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '60vh' }}>
          <CircularProgress />
        </Box>
      );
    }

    if (error) {
      return <Alert severity="error" sx={{ borderRadius: 2 }}>{error}</Alert>;
    }

    if (!doc) return null;

    if (isPdf(name, type)) {
      return (
        <Box
          component="iframe"
          title={name}
          src={doc.url}
          sx={{ width: '100%', height: '70vh', border: 0, borderRadius: 2, bgcolor: 'background.paper' }}
        />
      );
    }

    if (isDocx(name, type)) {
      return (
        <Box
          ref={docxRef}
          sx={{ height: '70vh', overflow: 'auto', bgcolor: 'background.paper', borderRadius: 2, p: 1 }}
        />
      );
    }

    if (isImage(name, type)) {
      return (
        <Box sx={{ display: 'flex', justifyContent: 'center', maxHeight: '70vh', overflow: 'auto' }}>
          <Box component="img" src={doc.url} alt={name} sx={{ maxWidth: '100%' }} />
        </Box>
      );
    }

    return (
      <Alert severity="info" sx={{ borderRadius: 2 }}>
        Náhľad pre tento typ súboru nie je podporovaný — dokument si môžete stiahnuť.
      </Alert>
    );
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      PaperProps={{ sx: { borderRadius: 3 } }}
    >
      <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', py: 2 }}>
        <Typography variant="h6" sx={{ fontWeight: 600 }} noWrap>
          {name || 'Náhľad dokumentu'}
        </Typography>
        <IconButton onClick={onClose} size="small" sx={{ bgcolor: 'action.hover' }}>
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>

      <Divider />

      <DialogContent dividers sx={{ bgcolor: 'background.default' }}>
        <Stack spacing={2}>
          <Alert severity="warning" sx={{ borderRadius: 2 }}>
            Zobrazuje sa <strong style={{ fontWeight: 600 }}>pôvodný, neanonymizovaný</strong> dokument.
            Prístup je zaznamenaný v bezpečnostnom audite.
          </Alert>
          {renderBody()}
        </Stack>
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2 }}>
        {doc && (
          <Button
            component="a"
            href={doc.url}
            download={name || 'dokument'}
            startIcon={<DownloadIcon />}
            sx={{ borderRadius: 2, fontWeight: 600 }}
          >
            Stiahnuť
          </Button>
        )}
        <Button onClick={onClose} variant="outlined" sx={{ borderRadius: 2, fontWeight: 600 }}>
          Zavrieť
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ValidationDocumentPreviewDialog;
